import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon, { IconProps } from "@insite/mobius/Icon/Icon";
import AlertOctagon from "@insite/mobius/Icons/AlertOctagon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import Info from "@insite/mobius/Icons/Info";
import Tag from "@insite/mobius/Icons/Tag";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { contentSpacingStyles } from "@store/theme/common/mixins";
import { COLORS } from "@store/theme/common/styleguide/config/colors";
import { TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import Color from "color";
import parse from "html-react-parser";
import React, { useState } from "react";
import styled, { css } from "styled-components";

const enum fields {
    type = "type",
    size = "size",
    showIcon = "showIcon",
    content = "content",
}

export const enum InfosBoxType {
    Warning = "warning",
    Error = "error",
    Success = "success",
    Promotion = "promotion",
    Default = "default",
}

export const enum InfosBoxSize {
    Small = "small",
    Medium = "medium",
    LargeVertical = "largeVertical",
}

interface FieldProps {
    [fields.type]?: InfosBoxType;
    [fields.size]?: InfosBoxSize;
    [fields.showIcon]?: boolean;
    [fields.content]:
        | JSX.Element
        | string
        | string[]
        | React.DetailedReactHTMLElement<{}, HTMLElement>
        | Array<React.DetailedReactHTMLElement<{}, HTMLElement>>;
}

export interface InfosBoxProps {
    fields: FieldProps;
    extendedStyles?: InfosBoxStyles;
}

export interface InfosBoxPartialProps extends Omit<InfosBoxProps, "fields"> {
    fields?: Partial<FieldProps>;
}

interface InfosBoxIconProps extends IconProps {
    spacing?: string;
    sizeDefinition?: InfosBoxSize;
    infosBoxSizeName?: string;
}

export interface InfosBoxStyles {
    wrapper?: InjectableCss;
    icon?: InfosBoxIconProps;
}

export interface CustomWrapperStylesProps extends InjectableCss {
    type: string;
    size: string;
}

export const infosBoxStyles: InfosBoxStyles = {
    wrapper: {},
    icon: {
        src: AlertTriangle,
    },
};

const typeDefinitions: any = {
    warning: {
        src: AlertTriangle,
        color: COLORS.text.base,
        iconColor: COLORS.warning,
        backgroundColor: Color(COLORS.warning).lighten(0.5).hex(),
    },
    error: {
        src: AlertOctagon,
        color: COLORS.text.base,
        iconColor: COLORS.error,
        backgroundColor: Color(COLORS.error).lighten(0.85).hex(),
    },
    success: {
        src: Check,
        color: COLORS.success.base,
        iconColor: COLORS.success.base,
        backgroundColor: COLORS.success.light,
    },
    promotion: {
        src: Tag,
        color: COLORS.success.base,
        iconColor: COLORS.success.base,
        backgroundColor: COLORS.success.light,
    },
    default: {
        src: Info,
        color: COLORS.info.dark,
        iconColor: COLORS.info.dark,
        backgroundColor: COLORS.info.base,
    },
};

const sizeDefinitions: any = {
    small: {
        spacing: "0.5rem",
        iconSize: 25,
    },
    medium: {
        spacing: "1rem",
        iconSize: 35,
    },
    largeVertical: {
        spacing: "1rem",
        iconSize: 45,
    },
};

const Wrapper = styled(StyledWrapper)<CustomWrapperStylesProps>`
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: ${props => (props.size === InfosBoxSize.LargeVertical ? "column" : "row")};
    text-align: ${props => (props.size === InfosBoxSize.LargeVertical ? "center" : "left")};
    padding: ${props => (props.size === InfosBoxSize.LargeVertical ? ".5rem 2rem 2rem" : sizeDefinitions[props.size].spacing)};
    height: ${props => (props.size === InfosBoxSize.LargeVertical ? "338px" : "auto")};
    justify-content: ${props => (props.size === InfosBoxSize.LargeVertical ? "center" : "auto")};
    background-color: ${props => typeDefinitions[props.type].backgroundColor};
    color: ${props => typeDefinitions[props.type].color};
    ${contentSpacingStyles("1em")};

    ${props =>
        breakpointValuesMediaQueries(
            props.theme,
            [{ values: [TYPOGRAPHY.size.smallest, TYPOGRAPHY.size.small], properties: ["font-size"] }],
            mediaOptions.min,
        )}

    li {
        line-height: ${TYPOGRAPHY.lineHeight.small};

        &:not(:last-child) {
            margin-bottom: 0.25em;
        }
    }

    ${injectCss}
`;

const InfosBoxIcon = styled(Icon)<InfosBoxIconProps>`
    margin-bottom: ${props => props.infosBoxSizeName === InfosBoxSize.LargeVertical ? props.spacing : "0"};
    margin-right: ${props => props.infosBoxSizeName !== InfosBoxSize.LargeVertical ? props.spacing : "0"};
    ${injectCss}
`;

export const InfosBox: React.FC<InfosBoxProps> = ({
    fields: { type = InfosBoxType.Default, size = InfosBoxSize.Medium, showIcon = true, content },
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeToNew(infosBoxStyles, extendedStyles));
    const typeDefinition = typeDefinitions[type];
    const sizeDefinition = sizeDefinitions[size];

    if (!content || !typeDefinition) {
        return null;
    }

    return (
        <Wrapper type={type} size={size} {...styles.wrapper}>
            {showIcon && (
                <InfosBoxIcon
                    {...styles.icon}
                    color={typeDefinition.iconColor}
                    size={sizeDefinition.iconSize}
                    spacing={sizeDefinition.spacing}
                    src={typeDefinition.src}
                    sizeDefenition={sizeDefinition}
                    infosBoxSizeName={size}
                />
            )}
            {typeof content === "string" ? parse(content, parserOptions) : content}
        </Wrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfosBox,
    definition: {
        displayName: "Information Box",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.type,
                displayName: "Information Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: InfosBoxType.Default, displayName: "Default" },
                    { value: InfosBoxType.Warning, displayName: "Warning" },
                    { value: InfosBoxType.Error, displayName: "Error" },
                    { value: InfosBoxType.Success, displayName: "Success" },
                ],
                defaultValue: InfosBoxType.Default,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.size,
                displayName: "Box Size",
                editorTemplate: "DropDownField",
                options: [
                    { value: InfosBoxSize.Small, displayName: "Default" },
                    { value: InfosBoxSize.Medium, displayName: "Warning" },
                    { value: InfosBoxSize.LargeVertical, displayName: "Large Vertical Warning" },
                ],
                defaultValue: InfosBoxSize.Medium,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.content,
                displayName: "Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 4,
            },
        ],
    },
};

export default widgetModule;
